import { createContext, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { userAttributesService } from '../../../shared/services/userAtributes/userAtributes';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/context/AuthContext';

export interface Vehicle {
  id: string;
  marca: string;
  modelo: string;
  tipo: 'carro' | 'moto' | 'caminhao' | 'outro';
  placa: string;
  ano: number;
  cor: string;
  observacoes?: string;
  foto?: string;
  tenantId: string;
  tenantName?: string;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

interface VehicleContextData {
  // Listagem
  vehicles: Vehicle[];
  isLoading: boolean;
  loading: boolean;
  error: string | null;
  handleCreate: () => void;
  handleView: (vehicle: Vehicle) => void;
  handleCloseError: () => void;
  
  // Modal de Delete
  isDeleteModalOpen: boolean;
  selectedVehicle: Vehicle | null;
  openDeleteModal: (vehicle: Vehicle) => void;
  closeDeleteModal: () => void;
  handleDeleteVehicle: () => void;
  isDeleting: boolean;
  
  // Navegação
  navigateToEdit: (id: string) => void;

  // Form
  vehicleData: Vehicle | null;
  isDataLoading: boolean;
  createVehicle: (data: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateVehicle: (id: string, data: Partial<Vehicle>) => Promise<void>;
  isSubmitting: boolean;
  getVehicleById: (id: string) => Vehicle;

  // Funções adicionais baseadas no código fornecido
  handleEdit: (idx: number, vehicle: any) => void;
  handleEditSave: (vehicleEditado: any) => void;
  handleDelete: () => void;
  processVehiclesData: (data: any) => Vehicle[];
  
  // Estados de edição
  editIdx: number | null;
  editInitial: any;
  openEdit: boolean;
  deleteIdx: number | null;
  openConfirm: boolean;

  setError: (error: string | null) => void;
}

interface VehicleProviderProps {
  children: ReactNode;
}

export const VehicleContext = createContext({} as VehicleContextData);

export function VehicleProvider({ children }: VehicleProviderProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { user: authUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  // Query para listar veículos
  const { data: vehicles = [], isLoading } = useQuery({
    queryKey: ['vehicles', authUser?.id],
    queryFn: async () => {
      try {
        if (!authUser?.id) {
          throw new Error('Usuário não autenticado');
        }
        // Buscar veículos através dos atributos do usuário
        const response = await userAttributesService.get(authUser.id, 'veiculo');
        
        // Processar a resposta para extrair os veículos
        if (response.data && response.data.value && Array.isArray(response.data.value)) {
          return response.data.value.map((vehicle: any) => ({
            ...vehicle,
            vehicleId: vehicle.id,
            attributeId: response.data.id,
            key: response.data.key,
            userId: response.data.userId
          }));
        }
        
        return [];
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar veículos';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar veículos');
        }
        return [];
      }
    },
    enabled: !!authUser?.id,
  });

  // Query para buscar dados do veículo no modo de edição
  const { data: vehicleData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['vehicle', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        // O id na URL agora é o attributeId, então podemos usar getById
        const response = await userAttributesService.getById(id);
        
        // Processar a resposta para extrair o veículo
        if (response.data && response.data.value && Array.isArray(response.data.value) && response.data.value.length > 0) {
          const vehicle = response.data.value[0];
          return {
            ...vehicle,
            vehicleId: vehicle.id,
            attributeId: response.data.id,
            key: response.data.key,
            userId: response.data.userId
          };
        }
        
        return null;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar veículo';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar veículo');
        }
        return null;
      }
    },
    enabled: !!id,
  });

  // Estado do modal de delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  // Estados de edição baseados no código fornecido
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [editInitial, setEditInitial] = useState<any>(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
  const [openConfirm, setOpenConfirm] = useState(false);

  // Mutation para deletar veículo
  const deleteMutation = useMutation({
    mutationFn: (id: string) => userAttributesService.removeValue(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', authUser?.id] });
      navigate('/vehicles');
    }
  });

  // Handlers para listagem
  const handleCreate = () => {
    navigate('/vehicles/new');
  };

  const handleView = (vehicle: Vehicle) => {
    navigate(`/vehicles/${vehicle.attributeId}`,{
      state: {
        vehicle: vehicle
      }
    });
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Handlers para modal de delete
  const openDeleteModal = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedVehicle(null);
  };

  const handleDeleteVehicle = () => {
    if (selectedVehicle?.id) {
      deleteMutation.mutate(selectedVehicle.id);
    }
  };

  // Handler para navegação
  const navigateToEdit = (id: string) => {
    navigate(`/vehicles/${id}/edit`);
  };

  // Mutations para criar e atualizar veículos
  const createMutation = useMutation({
    mutationFn: async (vehicleData: any) => {
      if (!authUser?.id) {
        throw new Error('Usuário não autenticado');
      }
      
      // Se tem arquivo para upload
      if (vehicleData.foto && vehicleData.foto.length > 0 && vehicleData.foto[0] instanceof File) {
        const formData = new FormData();
        formData.append('userId', authUser.id);
        formData.append('attributes[0].key', 'veiculo');

        // Adiciona os campos do veículo na estrutura correta
        formData.append('attributes[0].value[0].marca', vehicleData.marca);
        formData.append('attributes[0].value[0].modelo', vehicleData.modelo);
        formData.append('attributes[0].value[0].tipo', vehicleData.tipo);
        formData.append('attributes[0].value[0].placa', vehicleData.placa);
        formData.append('attributes[0].value[0].ano', vehicleData.ano);
        formData.append('attributes[0].value[0].cor', vehicleData.cor || '');
        formData.append('attributes[0].value[0].observacoes', vehicleData.observacoes || '');
        formData.append('file', vehicleData.foto[0]);
        
        return userAttributesService.createWithFile(formData);
      } else {
        const data = { ...vehicleData };
        delete data.foto;
        
        return userAttributesService.create({
          userId: authUser.id,
          attributes: [{
            key: 'veiculo',
            value: [data]
          }]
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', authUser?.id] });
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar veículo';
        setError(errorMessage);
      } else {
        setError('Erro ao criar veículo');
      }
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      if (!authUser?.id) {
        throw new Error('Usuário não autenticado');
      }
      
      const formData = new FormData();
      formData.append('userId', authUser.id);
      formData.append('attributes[0].key', 'veiculo');

      // Adiciona os campos do veículo na estrutura correta
      formData.append('attributes[0].value[0].marca', data.marca);
      formData.append('attributes[0].value[0].modelo', data.modelo);
      formData.append('attributes[0].value[0].tipo', data.tipo);
      formData.append('attributes[0].value[0].placa', data.placa);
      formData.append('attributes[0].value[0].ano', data.ano);
      formData.append('attributes[0].value[0].cor', data.cor || '');
      formData.append('attributes[0].value[0].observacoes', data.observacoes || '');
      
      // Se tem arquivo novo para upload
      if (data.foto && data.foto.length > 0 && data.foto[0] instanceof File) {
        formData.append('file', data.foto[0]);
        return userAttributesService.updateValueWithFile(id, formData);
      } else {
        return userAttributesService.updateValueWithFile(id, formData);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles', authUser?.id] });
      queryClient.invalidateQueries({ queryKey: ['vehicle', id] });
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar veículo';
        setError(errorMessage);
      } else {
        setError('Erro ao atualizar veículo');
      }
    }
  });

  const createVehicle = async (data: Omit<Vehicle, 'id' | 'createdAt' | 'updatedAt'>) => {
    await createMutation.mutateAsync(data);
  };

  const updateVehicle = async (id: string, data: Partial<Vehicle>) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const getVehicleById = (id: string) => {
    return vehicles.find((vehicle: any) => vehicle.id === id) || {} as Vehicle;
  };

  // Funções baseadas no código fornecido
  const handleEdit = (idx: number, vehicle: any) => {
    setEditIdx(idx);
    setEditInitial(vehicle);
    setOpenEdit(true);
  };

  const handleEditSave = (vehicleEditado: any) => {
    if (editIdx === null) return;
    
    updateMutation.mutate({ id: vehicleEditado.vehicleId, data: vehicleEditado });
    setOpenEdit(false);
    setEditIdx(null);
    setEditInitial(null);
  };

  const handleDelete = () => {
    if (deleteIdx === null || vehicles.length === 0) return;
    
    const vehicleToDelete = vehicles[deleteIdx];
    if (!vehicleToDelete || !vehicleToDelete.vehicleId) return;
    
    deleteMutation.mutate(vehicleToDelete.vehicleId);
    setOpenConfirm(false);
    setDeleteIdx(null);
  };

  const processVehiclesData = (data: any): Vehicle[] => {
    let processedVehicles: Vehicle[] = [];
    
    // Se é uma resposta direta do get (um único atributo)
    if (data && data.value && Array.isArray(data.value)) {
      data.value.forEach((item: any) => {
        processedVehicles.push({
          ...item,
          vehicleId: item.id,
          attributeId: data.id,
          key: data.key,
          userId: data.userId
        });
      });
    }
    
    // Se é uma lista de atributos (múltiplos atributos)
    if (data && Array.isArray(data)) {
      data.forEach((attribute: any) => {
        if (attribute.value && Array.isArray(attribute.value)) {
          attribute.value.forEach((item: any) => {
            processedVehicles.push({
              ...item,
              vehicleId: item.id,
              attributeId: attribute.id,
              key: attribute.key,
              userId: attribute.userId
            });
          });
        }
      });
    }
    
    return processedVehicles;
  };

  return (
    <VehicleContext.Provider
      value={{
        // Listagem
        vehicles,
        isLoading,
        loading: isLoading,
        error,
        handleCreate,
        handleView,
        handleCloseError,
        
        // Modal de Delete
        isDeleteModalOpen,
        selectedVehicle,
        openDeleteModal,
        closeDeleteModal,
        handleDeleteVehicle,
        isDeleting: deleteMutation.isPending,
        
        // Navegação
        navigateToEdit,

        // Form
        vehicleData,
        isDataLoading,
        createVehicle,
        updateVehicle,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
        getVehicleById,

        setError,

        // Funções adicionais
        handleEdit,
        handleEditSave,
        handleDelete,
        processVehiclesData,
        
        // Estados de edição
        editIdx,
        editInitial,
        openEdit,
        deleteIdx,
        openConfirm,
      }}
    >
      {children}
    </VehicleContext.Provider>
  );
} 